import { useState } from 'react';
import { QuickstartTheme } from '../../types/theme';
import { ResolvedDeviceFallbackConfig } from '../../types/deviceFallbackTheme';
import { ThemeLogo } from '../ThemeLogo';
import { FallbackStageMedia } from './FallbackStageMedia';
import { EmailLinkForm } from './EmailLinkForm';
import { buildHandoffUrl } from '../../utils/deviceHandoff';
import {
  CARD_EDGE_COLOR,
  CARD_FILL_ALPHA,
  MOBILE_PX,
  MOBILE_STAGE_WIDTH,
  mobileBlur,
  mobilePx,
  mobileScaled,
} from './mobileFallbackScale';
import { buildContinuumWash, buildMediaMask } from './mobileStageWash';
import { withAlpha } from './fallbackColor';
import type { SendHandoffEmailResult } from '../../../../api/modules/deviceHandoff';

interface MobileEmailFallbackProps {
  theme: QuickstartTheme;
  config: ResolvedDeviceFallbackConfig;
  persistentId: string | undefined;
  originDeviceType: 'ios' | 'android' | 'desktop';
  onSendEmail: (email: string) => Promise<SendHandoffEmailResult>;
  onCopyLink: () => void;
}

/**
 * Shown when a desktop-locked campaign is opened on a phone.
 *
 * Emailing a link is the primary continuation because the visitor cannot reach a
 * computer from here; copying is kept as a quiet escape hatch rather than a second CTA,
 * so the screen reads as a redirect rather than a rejection.
 */
export const MobileEmailFallback = ({
  theme,
  config,
  persistentId,
  originDeviceType,
  onSendEmail,
  onCopyLink,
}: MobileEmailFallbackProps) => {
  const [copyStatus, setCopyStatus] = useState<'idle' | 'success' | 'failed'>('idle');
  const [manualUrl, setManualUrl] = useState('');

  const handleCopy = async () => {
    if (!persistentId) return;

    const handoffUrl = buildHandoffUrl(persistentId, 'copy', originDeviceType);
    setManualUrl(handoffUrl);
    try {
      await navigator.clipboard.writeText(handoffUrl);
      setCopyStatus('success');
      onCopyLink();
      window.setTimeout(() => setCopyStatus('idle'), 2500);
    } catch (error) {
      // In-app webviews and unfocused documents reject writeText. Falling back to
      // showing the URL keeps this screen from dead-ending the visitor.
      // Analytic stays quiet here — the copy did not succeed.
      console.warn('[DeviceHandoff] Clipboard write failed:', error);
      setCopyStatus('failed');
    }
  };

  return (
    <div
      className='relative flex min-h-screen w-full flex-col overflow-y-auto font-["Inter"]'
      style={{
        backgroundColor: config.stageColor,
        ['--stage-w' as string]: MOBILE_STAGE_WIDTH,
      }}
    >
      {/* Framed for the visible band rather than the phone: top-anchored with a mild zoom,
          then masked away down the screen. Shaping it with a mask rather than by cropping
          the element means there is no edge to hide, so the picture thins out instead of
          stopping — which is the failure the design notes single out. */}
      <div
        className='absolute inset-0'
        style={{ maskImage: buildMediaMask(), WebkitMaskImage: buildMediaMask() }}
      >
        <FallbackStageMedia
          theme={theme}
          blur={mobileBlur(config.backgroundBlurPx)}
          anchor='top'
        />
      </div>

      {/* A long fall-off rather than a crop line: the game dissolves into the continuum,
          and only the lowest band goes fully solid to carry the copy. */}
      <div
        className='pointer-events-none absolute inset-0'
        style={{ background: buildContinuumWash(config.stageColor) }}
      />

      <div className='relative flex min-h-screen flex-col items-center px-6 py-8'>
        {/* The logos own the band between the top of the screen and the headline, and sit
            centred in it rather than pinned to the top edge. This band absorbs all the
            spare height, so the game reads through it at any phone size. */}
        <div className='flex w-full min-w-0 flex-1 flex-col items-center justify-center' style={{ gap: mobilePx(MOBILE_PX.brandGap) }}>
          <ThemeLogo
            kind='studio'
            theme={theme}
            style={{ height: mobileScaled(config.mobileStudioLogoScale), maxWidth: mobileScaled(0.5) }}
          />
          <ThemeLogo
            kind='game'
            theme={theme}
            style={{ height: mobileScaled(config.mobileGameLogoScale), maxWidth: mobileScaled(0.72) }}
          />
        </div>

        {/* Spaced by explicit offsets rather than one flex gap: the artboard sets the copy
            further off the card than the card is off the quiet action below it. */}
        <div className='flex w-full max-w-sm flex-col items-center'>
          <div className='flex flex-col items-center text-center' style={{ gap: mobilePx(MOBILE_PX.headlineGap) }}>
            <h1
              className='font-bold leading-tight'
              style={{ color: config.qrRimStartColor, fontSize: mobilePx(MOBILE_PX.headline) }}
            >
              {config.mobileHeadline}
            </h1>
            <p
              className='leading-relaxed'
              style={{ color: config.copyColor, fontSize: mobilePx(MOBILE_PX.subcopy) }}
            >
              {config.mobileSubcopy}
            </p>
          </div>

          <div
            className='w-full shadow-2xl'
            style={{
              backgroundColor: withAlpha(config.stageColor, CARD_FILL_ALPHA),
              border: `1px solid ${CARD_EDGE_COLOR}`,
              borderRadius: mobilePx(MOBILE_PX.cardRadius),
              padding: mobilePx(MOBILE_PX.cardPadding),
              marginTop: mobilePx(MOBILE_PX.copyBlockGap),
            }}
          >
            <EmailLinkForm config={config} onSubmit={onSendEmail} />
          </div>

          <div
            className='flex flex-col items-center'
            style={{ gap: mobilePx(MOBILE_PX.copyLinkRowGap), marginTop: mobilePx(MOBILE_PX.copyLinkGap) }}
          >
            <button
              type='button'
              onClick={handleCopy}
              // Copying needs an ID to embed, and handleCopy bails without one. Reflecting
              // that here keeps it from reading as a button that silently does nothing.
              disabled={!persistentId}
              style={{
                color: withAlpha(config.copyColor, 0.45),
                fontSize: mobilePx(MOBILE_PX.quietAction),
              }}
              className='transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40'
            >
              {copyStatus === 'success' ? 'Link Copied' : config.mobileCopyLinkLabel}
            </button>

            {copyStatus === 'failed' && (
              <p
                className='max-w-full break-all text-center'
                style={{
                  color: withAlpha(config.copyColor, 0.6),
                  fontSize: mobilePx(MOBILE_PX.fineNote),
                }}
              >
                Copy this link manually: <span className='select-all'>{manualUrl}</span>
              </p>
            )}
          </div>
        </div>

        {/* Trails the copy block rather than claiming its own band. All the slack lives
            above the logos, which is what bottom-weights the copy the way the design has it. */}
        <div style={{ marginTop: mobilePx(MOBILE_PX.poweredByGap) }}>
          <ThemeLogo kind='poweredBy' theme={theme} style={{ height: mobilePx(MOBILE_PX.poweredBy) }} />
        </div>
      </div>
    </div>
  );
};
